import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqs = [
  { q: 'Does Bonk support Rekordbox 6 and 7 databases?', a: 'Yes. Bonk syncs directly with Rekordbox database workflows so playlists, cues, and metadata stay aligned.' },
  { q: 'What are Quick Tag and Watch Folder for?', a: 'Quick Tag is for fast folder-scoped edits, while Watch Folder continuously ingests and enriches newly added tracks.' },
  { q: 'Can I plan sets in Bonk?', a: 'Yes. Graph Playlist helps design transitions visually, and Chapter Builder helps structure energy arcs with reusable sections.' },
  { q: 'What insights come from Analytics and Benchmark Center?', a: 'Analytics gives library-level trends for set prep. Benchmark Center measures scan/sort/cache performance on your machine.' },
  { q: 'What changed in downloading?', a: 'Recent downloader improvements make queue handling and settings flows cleaner for supported sources like Tidal and Qobuz.' },
  { q: 'How can I get updates?', a: 'Use the waitlist and contact forms below. They post directly to Bonk site API endpoints for follow-up.' },
];

export default function FAQ() {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  const [open, setOpen] = useState(-1);

  return (
    <section id="faq" data-testid="faq-section" ref={ref} style={{ padding: 'clamp(120px,14vw,200px) 0', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80, alignItems: 'start' }} className="faq-grid">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, color: '#52525B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>FAQ</span>
            <h2 data-testid="faq-title" style={{ fontFamily: 'var(--heading)', fontWeight: 800, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', marginTop: 16 }}>
              Questions<br /><span style={{ color: '#52525B' }}>&amp; answers</span>
            </h2>
          </motion.div>

          <div>
            {faqs.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <button data-testid={`faq-toggle-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 0', textAlign: 'left', color: open === i ? '#FAFAFA' : '#A1A1AA', transition: 'color .2s' }}>
                  <span style={{ fontFamily: 'var(--heading)', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>{f.q}</span>
                  <ChevronDown size={16} color="#52525B" style={{ flexShrink: 0, transition: 'transform .3s', transform: open === i ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }} style={{ overflow: 'hidden' }}>
                      <p data-testid={`faq-answer-${i}`} style={{ color: '#71717A', fontSize: 14, lineHeight: 1.8, paddingBottom: 20 }}>{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media(max-width:768px){.faq-grid{grid-template-columns:1fr !important;gap:40px !important}}`}</style>
    </section>
  );
}
